/**
 * Generic (unbranded) fermentables — adjunct grains, sugars, extracts and fruit.
 *
 * These are *class* entries rather than products: nobody publishes a spec sheet
 * for "flaked oats", so every entry here is `maker: 'Generic'` and
 * {@link provenanceSchema | provenance} `representative` by definition. The
 * numbers are the mid-point of what the class typically delivers, good enough
 * to design a recipe against and never to be quoted as a supplier figure. Where
 * a brewer has the actual bag in front of them, the branded entries elsewhere
 * in this package should win.
 *
 * Sugars, syrups and extracts follow the schema convention literally:
 * `extractFineDryPercent` is the *dry-basis* extract, so refined sugars sit at
 * 100 % and all of their real-world dilution is carried by `moisturePercent`.
 * That is why dextrose (sold as the monohydrate, ~9 % water of crystallisation)
 * lands at 42 PPG rather than sucrose's 46.214, why liquid malt extract at ~20 %
 * water yields roughly four fifths of what dry malt extract does, and why the
 * candi syrups sit lower still. Fruit purées are the one awkward case: a purée
 * is really ~88 % water, which is past the schema's 60 % ceiling on
 * `moisturePercent`, so they are entered at that ceiling with a dry-basis
 * extract picked to land the *as-is* yield near a tenth of sucrose — about
 * 4–5 PPG, which is what a purée genuinely contributes.
 */

import { type Fermentable, lovibondToEbc } from '../../schema/fermentable';

/**
 * An aseptic fruit purée, entered the way the file header explains: moisture
 * is capped at the schema's 60 %, and dry-basis extract is chosen so the
 * as-is yield lands around 4–6 PPG.
 */
function fruitPuree(args: {
  readonly id: string;
  readonly name: string;
  readonly extractFineDryPercent: number;
  readonly colorLovibond: number;
  readonly description: string;
  readonly tags: readonly string[];
}): Fermentable {
  return {
    id: args.id,
    name: args.name,
    maker: 'Generic',
    origin: 'Various',
    type: 'fruit',
    maltClass: 'non-buffering',
    extractFineDryPercent: args.extractFineDryPercent,
    moisturePercent: 60,
    colorLovibond: args.colorLovibond,
    colorEbc: Math.round(lovibondToEbc(args.colorLovibond) * 10) / 10,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description: args.description,
    tags: [...args.tags],
    provenance: 'representative',
  };
}

/** Unbranded class-typical fermentables. @see Fermentable */
export const genericFermentables: readonly Fermentable[] = [
  {
    id: 'flaked-oats',
    name: 'Flaked Oats',
    maker: 'Generic',
    origin: 'Various',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 78,
    moisturePercent: 9,
    colorLovibond: 1,
    colorEbc: 1.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Steamed and rolled oats, added for the silky mouthfeel and dense head their beta-glucans and lipids build. They carry no enzymes of their own and turn the mash gummy above roughly 20 %, so pair them with a high-DP base malt and rice hulls.',
    tags: ['adjunct', 'flaked', 'oats', 'mouthfeel', 'hazy', 'stout'],
    provenance: 'representative',
  },
  {
    id: 'flaked-wheat',
    name: 'Flaked Wheat',
    maker: 'Generic',
    origin: 'Various',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 84,
    moisturePercent: 9,
    colorLovibond: 1.6,
    colorEbc: 3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 40,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted wheat pre-gelatinised between hot rollers so it converts in a normal single-infusion mash. The protein it brings gives witbier its haze and lacing; it is grainier and sharper than malted wheat and offers no enzymes.',
    tags: ['adjunct', 'flaked', 'wheat', 'witbier', 'head-retention'],
    provenance: 'representative',
  },
  {
    id: 'flaked-barley',
    name: 'Flaked Barley',
    maker: 'Generic',
    origin: 'Various',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 76,
    moisturePercent: 9,
    colorLovibond: 1.5,
    colorEbc: 2.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Rolled unmalted barley, the traditional third of an Irish stout grist alongside pale malt and roasted barley. Adds a creamy body, a grainy bite and excellent foam stability; keep it under about 20 % or lautering slows badly.',
    tags: ['adjunct', 'flaked', 'barley', 'stout', 'body', 'irish'],
    provenance: 'representative',
  },
  {
    id: 'flaked-maize',
    name: 'Flaked Maize',
    maker: 'Generic',
    origin: 'Various',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 88,
    moisturePercent: 9,
    colorLovibond: 1,
    colorEbc: 1.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 40,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pre-gelatinised corn flakes, the classic way to thin the body and lighten the color of a beer without dropping gravity. High yield and a faint sweetcorn note; the standard adjunct in American lager, cream ale and English mild.',
    tags: ['adjunct', 'flaked', 'corn', 'maize', 'light-body', 'lager'],
    provenance: 'representative',
  },
  {
    id: 'flaked-rice',
    name: 'Flaked Rice',
    maker: 'Generic',
    origin: 'Various',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 90,
    moisturePercent: 9,
    colorLovibond: 0.4,
    colorEbc: 0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Rolled rice, the most neutral adjunct available: very high extract, effectively no color and no flavor of its own. Dries and thins a pale lager even more cleanly than maize, which is why it defines the crispest American and Japanese examples.',
    tags: ['adjunct', 'flaked', 'rice', 'neutral', 'light-body', 'lager'],
    provenance: 'representative',
  },
  {
    id: 'instant-rice',
    name: 'Instant Rice (Minute Rice)',
    maker: 'Generic',
    origin: 'Various',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 88,
    moisturePercent: 10,
    colorLovibond: 0.4,
    colorEbc: 0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Supermarket instant rice, cooked and dried so the starch is already gelatinised: it goes straight into the mash with no cereal cook, which is why homebrewers reach for it in a light lager. Behaves like flaked rice, with near-full extract, no color and no flavor of its own. Buy plain white rice with nothing added.',
    tags: ['adjunct', 'rice', 'instant', 'minute-rice', 'neutral', 'light-body', 'lager'],
    provenance: 'representative',
  },
  {
    id: 'torrefied-wheat',
    name: 'Torrefied Wheat',
    maker: 'Generic',
    origin: 'United Kingdom',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 85,
    moisturePercent: 7,
    colorLovibond: 1.5,
    colorEbc: 2.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 40,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Whole wheat heated until the kernels puff and the starch gelatinises, leaving a light nutty note that flaked wheat lacks. A British brewhouse staple for head retention in bitter and mild; mill it lightly and mash as normal.',
    tags: ['adjunct', 'wheat', 'torrefied', 'british', 'head-retention'],
    provenance: 'representative',
  },
  {
    id: 'raw-wheat',
    name: 'Raw Wheat',
    maker: 'Generic',
    origin: 'Various',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 86,
    moisturePercent: 11,
    colorLovibond: 1.6,
    colorEbc: 3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 40,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted, unflaked wheat berries as used in witbier and lambic. The starch is not pre-gelatinised, so a cereal mash or a protein/beta-glucanase rest is worth the trouble; it repays it with intense haze, tart grain character and huge foam.',
    tags: ['adjunct', 'wheat', 'raw', 'unmalted', 'witbier', 'lambic'],
    provenance: 'representative',
  },
  {
    id: 'malted-oats',
    name: 'Malted Oats',
    maker: 'Generic',
    origin: 'Various',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 78,
    moisturePercent: 6,
    colorLovibond: 2.5,
    colorEbc: 5.4,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Oats taken through a full malting rather than simply steamed, so they retain a little diastatic power and a rounder, biscuity flavor. Smoother and less gluey in the mash than flaked oats while giving much the same creamy weight.',
    tags: ['adjunct', 'oats', 'malted', 'mouthfeel', 'hazy'],
    provenance: 'representative',
  },
  {
    id: 'rice-hulls',
    name: 'Rice Hulls',
    maker: 'Generic',
    origin: 'Various',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 0,
    moisturePercent: 8,
    colorLovibond: 0,
    colorEbc: 0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A purely mechanical lautering aid: inert husks that hold the grain bed open and contribute no extract, color or flavor whatsoever. Add 2–5 % of the grist weight whenever oats, rye or a large wheat fraction threatens a stuck sparge.',
    tags: ['adjunct', 'rice-hulls', 'lautering', 'no-extract', 'process-aid'],
    provenance: 'representative',
  },
  {
    id: 'corn-grits',
    name: 'Corn Grits',
    maker: 'Generic',
    origin: 'Various',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 88,
    moisturePercent: 12,
    colorLovibond: 0.4,
    colorEbc: 0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 40,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Degermed, coarsely ground corn — the cheap high-yield adjunct of the industrial brewhouse. Unlike flaked maize the starch is raw, so it needs a cereal cooker or a boiled sub-mash with a little base malt before it joins the main mash.',
    tags: ['adjunct', 'corn', 'grits', 'cereal-mash', 'lager'],
    provenance: 'representative',
  },
  {
    id: 'roasted-barley',
    name: 'Roasted Barley',
    maker: 'Generic',
    origin: 'Various',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 65,
    moisturePercent: 5,
    colorLovibond: 300,
    colorEbc: 794,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted barley roasted to near-black, and the source of the dry coffee bitterness and ruby-black color of Irish stout. Sharper and drier than chocolate malt; 3–8 % is the usual working range and it acidifies the mash strongly.',
    tags: ['roasted', 'barley', 'unmalted', 'stout', 'coffee', 'dry'],
    provenance: 'representative',
  },
  {
    id: 'dextrose',
    name: 'Dextrose (Corn Sugar)',
    maker: 'Generic',
    origin: 'Various',
    type: 'sugar',
    maltClass: 'non-buffering',
    extractFineDryPercent: 100,
    moisturePercent: 9,
    colorLovibond: 0,
    colorEbc: 0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Glucose from corn starch, sold as the monohydrate — the crystal carries about 9 % water of crystallization, which is why it yields roughly 42 points where sucrose gives 46. Ferments out completely and quickly, drying the finish and lifting alcohol without body.',
    tags: ['sugar', 'dextrose', 'glucose', 'corn-sugar', 'dry-finish', 'priming'],
    provenance: 'representative',
  },
  {
    id: 'sucrose',
    name: 'Table Sugar (Sucrose)',
    maker: 'Generic',
    origin: 'Various',
    type: 'sugar',
    maltClass: 'non-buffering',
    extractFineDryPercent: 100,
    moisturePercent: 0,
    colorLovibond: 0,
    colorEbc: 0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Refined cane or beet sugar, the reference point every extract figure in this catalog is measured against at 46.214 points per pound per gallon. Yeast inverts it readily and ferments it dry; the standard way to boost a Belgian ale without thickening it.',
    tags: ['sugar', 'sucrose', 'cane', 'dry-finish', 'belgian'],
    provenance: 'representative',
  },
  {
    id: 'invert-sugar-no-1',
    name: 'Invert Sugar No. 1',
    maker: 'Generic',
    origin: 'United Kingdom',
    type: 'sugar',
    maltClass: 'non-buffering',
    extractFineDryPercent: 100,
    moisturePercent: 23,
    colorLovibond: 6,
    colorEbc: 14.7,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest grade of the traditional British invert scale: cane syrup hydrolyzed to glucose and fructose and cooked only briefly, so it sits a shade or two darker than clear sugar. Faint honey and light caramel, and it ferments out as completely as table sugar. About a quarter water, so it yields near 36 points per pound rather than sucrose 46. At home in an ordinary bitter, a best bitter or a pale mild.',
    tags: ['sugar', 'invert', 'syrup', 'british', 'pale', 'bitter'],
    provenance: 'representative',
  },
  {
    id: 'invert-sugar-no-2',
    name: 'Invert Sugar No. 2',
    maker: 'Generic',
    origin: 'United Kingdom',
    type: 'sugar',
    maltClass: 'non-buffering',
    extractFineDryPercent: 100,
    moisturePercent: 23,
    colorLovibond: 14,
    colorEbc: 35.9,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The second grade of the British invert scale, cooked on from No. 1 until it turns amber: light toffee and a suggestion of dried fruit, without the liquorice edge No. 3 carries. The traditional sugar for best bitter, ESB and dark mild, and the period-correct choice for a Victorian recipe. Roughly a quarter water, so near 36 points per pound.',
    tags: ['sugar', 'invert', 'syrup', 'british', 'amber', 'toffee'],
    provenance: 'representative',
  },
  {
    id: 'invert-sugar-no-3',
    name: 'Invert Sugar No. 3',
    maker: 'Generic',
    origin: 'United Kingdom',
    type: 'sugar',
    maltClass: 'non-buffering',
    extractFineDryPercent: 100,
    moisturePercent: 23,
    colorLovibond: 94.8,
    colorEbc: 250,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Cane syrup hydrolyzed into glucose and fructose and then cooked to a dark treacle color, the third grade of the traditional British scale. Brings dried fruit, toffee and a hint of liquorice to old ale, porter and strong bitter; roughly a quarter of it is water.',
    tags: ['sugar', 'invert', 'syrup', 'british', 'dark', 'treacle'],
    provenance: 'representative',
  },
  {
    id: 'lactose',
    name: 'Lactose (Milk Sugar)',
    maker: 'Generic',
    origin: 'Various',
    type: 'sugar',
    maltClass: 'non-buffering',
    extractFineDryPercent: 100,
    moisturePercent: 5,
    colorLovibond: 0,
    colorEbc: 0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Milk sugar, which brewing yeast cannot ferment at all — every point it adds stays in the finished beer as residual sweetness and body. Carried here as the monohydrate, so about 5 % of the weight is water of crystallization. Dissolve in the boil; 4–10 % suits a milk stout or a pastry sour.',
    tags: ['sugar', 'lactose', 'unfermentable', 'sweetness', 'body', 'milk-stout'],
    provenance: 'representative',
  },
  {
    id: 'maltodextrin',
    name: 'Maltodextrin',
    maker: 'Generic',
    origin: 'Various',
    type: 'sugar',
    maltClass: 'non-buffering',
    extractFineDryPercent: 100,
    moisturePercent: 5,
    colorLovibond: 0,
    colorEbc: 0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A tasteless chain of glucose units too long for brewing yeast to touch, so it reads on the hydrometer but never ferments. Used to rebuild body and mouthfeel in a beer that finished too thin, without adding the sweetness lactose brings.',
    tags: ['sugar', 'maltodextrin', 'unfermentable', 'body', 'mouthfeel'],
    provenance: 'representative',
  },
  {
    id: 'honey',
    name: 'Honey',
    maker: 'Generic',
    origin: 'Various',
    type: 'sugar',
    maltClass: 'non-buffering',
    extractFineDryPercent: 100,
    moisturePercent: 17,
    colorLovibond: 1,
    colorEbc: 1.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Roughly 17 % water and almost entirely fermentable glucose and fructose, so it dries a beer out hard and leaves far less flavor than brewers expect. Add it after the boil or at high krausen to keep the volatile floral aroma that is the whole point.',
    tags: ['sugar', 'honey', 'floral', 'dry-finish', 'mead'],
    provenance: 'representative',
  },
  {
    id: 'candi-syrup-simplicity',
    name: 'Belgian Candi Syrup Simplicity',
    maker: 'Generic',
    origin: 'Belgium',
    type: 'sugar',
    maltClass: 'non-buffering',
    extractFineDryPercent: 100,
    moisturePercent: 27,
    colorLovibond: 1,
    colorEbc: 1.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest grade on the candi syrup scale, around 1 Lovibond: fully inverted sugars with no color worth measuring and only a faint honey or citrus note. Effectively the syrup form of clear candi sugar, and the usual choice for a tripel, saison or Belgian golden strong where the sugar is there to dry the beer out rather than darken it. About a quarter water, so near 34 points per pound.',
    tags: ['sugar', 'candi', 'syrup', 'belgian', 'pale', 'tripel'],
    provenance: 'representative',
  },
  {
    id: 'candi-syrup-d-45',
    name: 'Belgian Candi Syrup D-45',
    maker: 'Generic',
    origin: 'Belgium',
    type: 'sugar',
    maltClass: 'non-buffering',
    extractFineDryPercent: 100,
    moisturePercent: 27,
    colorLovibond: 45,
    colorEbc: 118,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Sugar syrup cooked until Maillard reactions give it an amber color and a caramel, light-toffee flavor. About a quarter water, so it yields near 34 points per pound; the usual choice for adding depth to a dubbel or Belgian pale without heaviness.',
    tags: ['sugar', 'candi', 'syrup', 'belgian', 'caramel', 'amber'],
    provenance: 'representative',
  },
  {
    id: 'candi-syrup-d-90',
    name: 'Belgian Candi Syrup D-90',
    maker: 'Generic',
    origin: 'Belgium',
    type: 'sugar',
    maltClass: 'non-buffering',
    extractFineDryPercent: 100,
    moisturePercent: 27,
    colorLovibond: 90,
    colorEbc: 237.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      "The middle grade of Belgian candi syrup: dark caramel and toffee with mild dark fruit, fig and plum, well short of D-180's burnt-raisin intensity. The usual choice for a dubbel or Belgian dark strong where D-45 reads too light and D-180 too heavy, and still fully fermentable.",
    tags: ['sugar', 'candi', 'syrup', 'belgian', 'dark', 'caramel'],
    provenance: 'representative',
  },
  {
    id: 'candi-syrup-d-180',
    name: 'Belgian Candi Syrup D-180',
    maker: 'Generic',
    origin: 'Belgium',
    type: 'sugar',
    maltClass: 'non-buffering',
    extractFineDryPercent: 100,
    moisturePercent: 27,
    colorLovibond: 180,
    colorEbc: 476,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest common grade of Belgian candi syrup: raisin, date, plum and burnt caramel, with enough color that 5 % will visibly darken a beer. Fully fermentable despite the flavor intensity, which is how a dark strong ale stays dangerously drinkable.',
    tags: ['sugar', 'candi', 'syrup', 'belgian', 'dark', 'dark-fruit'],
    provenance: 'representative',
  },
  {
    id: 'candi-syrup-d-240',
    name: 'Belgian Candi Syrup D-240',
    maker: 'Generic',
    origin: 'Belgium',
    type: 'sugar',
    maltClass: 'non-buffering',
    extractFineDryPercent: 100,
    moisturePercent: 27,
    colorLovibond: 240,
    colorEbc: 634.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest candi syrup made, a third darker again than D-180: dark raisin, prune, date and roasted caramel, with enough color that two or three percent of the grist shows in the glass. Still fully fermentable despite the intensity, so it deepens a quadrupel or a Belgian dark strong without leaving sweetness behind. About a quarter water, so near 34 points per pound.',
    tags: ['sugar', 'candi', 'syrup', 'belgian', 'dark', 'dark-fruit'],
    provenance: 'representative',
  },
  {
    id: 'clear-candi-sugar',
    name: 'Clear Candi Sugar',
    maker: 'Generic',
    origin: 'Belgium',
    type: 'sugar',
    maltClass: 'non-buffering',
    extractFineDryPercent: 100,
    moisturePercent: 0,
    colorLovibond: 0,
    colorEbc: 0,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Rock candi crystals grown from a pure sucrose solution — chemically the same as table sugar and yielding the same 46 points, with no color or flavor of its own. Traditional in Belgian tripel and golden strong ale for a bone-dry, deceptively light finish.',
    tags: ['sugar', 'candi', 'rock', 'belgian', 'clear', 'tripel'],
    provenance: 'representative',
  },
  {
    id: 'golden-syrup',
    name: 'Golden Syrup',
    maker: 'Generic',
    origin: 'United Kingdom',
    type: 'sugar',
    maltClass: 'non-buffering',
    extractFineDryPercent: 100,
    moisturePercent: 17,
    colorLovibond: 8,
    colorEbc: 20,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Partially inverted cane syrup — the tinned syrup sold for baking, and a fair stand-in for a pale British invert when No. 1 is not to hand. Faint buttery caramel and vanilla over a pale gold color, and it ferments almost entirely away. About 17 % water, so it yields near 38 points per pound. Ten to fifteen percent thins the body of a bitter or a strong ale without leaving much flavor behind.',
    tags: ['sugar', 'syrup', 'british', 'invert', 'pale', 'caramel'],
    provenance: 'representative',
  },
  {
    id: 'black-treacle',
    name: 'Black Treacle',
    maker: 'Generic',
    origin: 'United Kingdom',
    type: 'sugar',
    maltClass: 'non-buffering',
    extractFineDryPercent: 88,
    moisturePercent: 25,
    colorLovibond: 100,
    colorEbc: 263.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Cane molasses cut back with refined syrup: nearly black, bitter with liquorice and burnt sugar, and far more assertive on the palate than the same weight of dark invert. About a quarter water, and enough of the solids are ash rather than sugar that it yields near 31 points per pound. Two to five percent builds a treacle stout or an old ale; much more and the bitterness runs the beer.',
    tags: ['sugar', 'syrup', 'british', 'treacle', 'dark', 'liquorice'],
    provenance: 'representative',
  },
  {
    id: 'molasses',
    name: 'Molasses',
    maker: 'Generic',
    origin: 'Various',
    type: 'sugar',
    maltClass: 'non-buffering',
    extractFineDryPercent: 74,
    moisturePercent: 23,
    colorLovibond: 80,
    colorEbc: 210.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'What is left after cane sugar is crystallized out, and the most aggressive of the traditional sugars: liquorice, burnt sugar, iron and a mineral bitterness that carries right through fermentation. Nearly a quarter water, and a real share of the solids is ash and non-sugar organics, so it yields only about 26 points per pound against sucrose 46. Two to five percent is plenty in a stout, a porter or a rum-barrel beer.',
    tags: ['sugar', 'syrup', 'molasses', 'dark', 'liquorice', 'mineral'],
    provenance: 'representative',
  },
  {
    id: 'demerara-sugar',
    name: 'Demerara Sugar',
    maker: 'Generic',
    origin: 'Various',
    type: 'sugar',
    maltClass: 'non-buffering',
    extractFineDryPercent: 100,
    moisturePercent: 2,
    colorLovibond: 3,
    colorEbc: 6.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Raw cane sugar spun only far enough to leave a thin molasses film on large pale amber crystals. Near enough pure sucrose that it yields what table sugar does, about 45 points per pound, with light toffee and a faint rum note from the film. Traditional in English strong ales and worth reaching for anywhere plain sugar would read as too clinical.',
    tags: ['sugar', 'raw-sugar', 'cane', 'demerara', 'toffee', 'amber'],
    provenance: 'representative',
  },
  {
    id: 'turbinado-sugar',
    name: 'Turbinado Sugar',
    maker: 'Generic',
    origin: 'Various',
    type: 'sugar',
    maltClass: 'non-buffering',
    extractFineDryPercent: 100,
    moisturePercent: 2,
    colorLovibond: 2,
    colorEbc: 4.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Raw cane sugar, and honestly a close cousin of demerara: the same partly refined crystals with a molasses film, spun a little cleaner so the color and the toffee note come out a shade lighter. Choosing between the two is a question of what the shop stocks rather than what it does to the beer — expect near-full sucrose yield, about 45 points per pound, and a whisper of toffee from either.',
    tags: ['sugar', 'raw-sugar', 'cane', 'turbinado', 'toffee', 'pale'],
    provenance: 'representative',
  },
  {
    id: 'light-brown-sugar',
    name: 'Light Brown Sugar',
    maker: 'Generic',
    origin: 'Various',
    type: 'sugar',
    maltClass: 'non-buffering',
    extractFineDryPercent: 99,
    moisturePercent: 3,
    colorLovibond: 15,
    colorEbc: 38.6,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Refined sugar with a light hand of cane molasses stirred back in: gentle toffee and caramel, a faint rum note, and none of the liquorice weight of the dark grade. About 3 % water and nearly all of the rest ferments, so it yields near 44 points per pound. Five to ten percent adds a little color and warmth to a brown ale or a Belgian pale without leaving sweetness behind.',
    tags: ['sugar', 'brown-sugar', 'molasses', 'toffee', 'caramel', 'light'],
    provenance: 'representative',
  },
  {
    id: 'dark-brown-sugar',
    name: 'Dark Brown Sugar',
    maker: 'Generic',
    origin: 'Various',
    type: 'sugar',
    maltClass: 'non-buffering',
    extractFineDryPercent: 98,
    moisturePercent: 3.5,
    colorLovibond: 40,
    colorEbc: 104.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Refined sugar with cane molasses stirred back in until it is damp and dark — toffee, liquorice and dried fruit, and far more flavor per pound than demerara. Three or four percent of the weight is water and almost all the rest ferments, so it yields near 44 points per pound. Five to fifteen percent suits an old ale, a strong mild or a Belgian dark strong.',
    tags: ['sugar', 'brown-sugar', 'molasses', 'toffee', 'dried-fruit', 'dark'],
    provenance: 'representative',
  },
  {
    id: 'maple-syrup',
    name: 'Maple Syrup',
    maker: 'Generic',
    origin: 'Various',
    type: 'sugar',
    maltClass: 'non-buffering',
    extractFineDryPercent: 100,
    moisturePercent: 33,
    colorLovibond: 35,
    colorEbc: 91.6,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Boiled-down maple sap: about a third water and essentially all sucrose, so it yields near 31 points per pound and dries a beer out much as table sugar does. The honest point is that almost none of the maple character survives primary fermentation — in the boil it reads as thin sweetness and nothing more. Add it late, at packaging or to an already fermented beer, if the aroma is what you are after.',
    tags: ['sugar', 'syrup', 'maple', 'dry-finish', 'late-addition', 'autumn'],
    provenance: 'representative',
  },
  {
    id: 'dme-extra-light',
    name: 'Dry Malt Extract — Extra Light',
    maker: 'Generic',
    origin: 'Various',
    type: 'extract',
    maltClass: 'non-buffering',
    extractFineDryPercent: 97,
    moisturePercent: 3,
    colorLovibond: 3,
    colorEbc: 6.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 100,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Spray-dried pilsner-and-pale-malt wort, only about 3 % water, so it yields close to 44 points per pound — the highest of any malt-derived fermentable. The default for starters and for extract batches where color must stay pale.',
    tags: ['extract', 'dme', 'dry', 'pale', 'starter'],
    provenance: 'representative',
  },
  {
    id: 'dme-amber',
    name: 'Dry Malt Extract — Amber',
    maker: 'Generic',
    origin: 'Various',
    type: 'extract',
    maltClass: 'non-buffering',
    extractFineDryPercent: 97,
    moisturePercent: 3,
    colorLovibond: 10,
    colorEbc: 25.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 100,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Dry extract built from a grist that includes crystal and kilned malt, giving a copper color and a caramel-biscuit backbone. Convenient for amber ales and bitters, though it takes away any control over how much of that character lands.',
    tags: ['extract', 'dme', 'dry', 'amber', 'caramel'],
    provenance: 'representative',
  },
  {
    id: 'lme-light',
    name: 'Liquid Malt Extract — Light',
    maker: 'Generic',
    origin: 'Various',
    type: 'extract',
    maltClass: 'non-buffering',
    extractFineDryPercent: 97,
    moisturePercent: 20,
    colorLovibond: 4,
    colorEbc: 9.4,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 100,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale wort concentrated to a syrup of roughly 80 % solids, so a pound gives about 36 points — four fifths of what the dry equivalent does. Darkens noticeably with age and with long boils, so add most of it late.',
    tags: ['extract', 'lme', 'liquid', 'syrup', 'pale'],
    provenance: 'representative',
  },
  {
    id: 'lme-dark',
    name: 'Liquid Malt Extract — Dark',
    maker: 'Generic',
    origin: 'Various',
    type: 'extract',
    maltClass: 'non-buffering',
    extractFineDryPercent: 97,
    moisturePercent: 20,
    colorLovibond: 30,
    colorEbc: 78.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 100,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Liquid extract made from a dark grist carrying crystal and roasted malt, at the same ~20 % moisture as the pale version. Delivers color, toffee and light roast in one addition for porters and brown ales.',
    tags: ['extract', 'lme', 'liquid', 'syrup', 'dark', 'porter'],
    provenance: 'representative',
  },
  fruitPuree({
    id: 'raspberry-puree',
    name: 'Raspberry Purée',
    extractFineDryPercent: 25,
    colorLovibond: 5,
    description:
      'Aseptic seedless raspberry purée, mostly water, so its as-is yield is only about a tenth of sucrose — plan for the gravity contribution to be small and the aroma to be the point. Add post-fermentation to protect the delicate perfume and expect a pink haze.',
    tags: ['fruit', 'raspberry', 'puree', 'berry', 'sour', 'aroma'],
  }),
  fruitPuree({
    id: 'sweet-cherry-puree',
    name: 'Sweet Cherry Purée',
    extractFineDryPercent: 28,
    colorLovibond: 6,
    description:
      'Destoned sweet cherry purée, a shade denser than raspberry but still overwhelmingly water. The sugars ferment out completely, leaving a dry, almond-tinged stone-fruit character that suits dark sours, dubbels and barrel-aged stouts.',
    tags: ['fruit', 'cherry', 'puree', 'stone-fruit', 'sour', 'kriek'],
  }),
  fruitPuree({
    id: 'tart-cherry-puree',
    name: 'Tart Cherry Purée',
    extractFineDryPercent: 24,
    colorLovibond: 10,
    description:
      'Morello / Montmorency purée, leaner and more acidic than the sweet cherry. The sugars ferment out and leave a bright, winey sour-cherry note — this is the kriek and Flanders-red fruit, not the pastry-stout one. Add after primary.',
    tags: ['fruit', 'cherry', 'puree', 'tart', 'sour', 'kriek'],
  }),
  fruitPuree({
    id: 'blackberry-puree',
    name: 'Blackberry Purée',
    extractFineDryPercent: 24,
    colorLovibond: 18,
    description:
      'Seedless blackberry, dark enough to stain a pale beer purple-black. Yield is still mostly water; the point is the bramble and a dry, winey finish in sours, porters and mixed-fermentation blends. Expect a lasting haze.',
    tags: ['fruit', 'blackberry', 'puree', 'berry', 'sour', 'dark'],
  }),
  fruitPuree({
    id: 'blueberry-puree',
    name: 'Blueberry Purée',
    extractFineDryPercent: 26,
    colorLovibond: 14,
    description:
      'Aseptic blueberry, quieter on the nose than raspberry and a little denser. The sugars disappear; what remains is a soft berry and a gray-purple tint that reads better in a wheat beer or a kettle sour than in a stout.',
    tags: ['fruit', 'blueberry', 'puree', 'berry', 'sour', 'wheat'],
  }),
  fruitPuree({
    id: 'strawberry-puree',
    name: 'Strawberry Purée',
    extractFineDryPercent: 22,
    colorLovibond: 4,
    description:
      'The most water-heavy of the common purées, and the aroma dies in the boil, so this belongs in the fermenter. Pink, fragile, and easy to overdose into candy; a kettle sour or a blonde is where a modest addition actually tastes like strawberry.',
    tags: ['fruit', 'strawberry', 'puree', 'berry', 'sour', 'aroma'],
  }),
  fruitPuree({
    id: 'peach-puree',
    name: 'Peach Purée',
    extractFineDryPercent: 26,
    colorLovibond: 3,
    description:
      'Ripe clingstone peach, pale and low in color. The sugars ferment clean; the pit-and-skin note is what you are buying. Berliner weisse, kettle sours and the fruitier farmhouse ales are the usual home. Add late and cold.',
    tags: ['fruit', 'peach', 'puree', 'stone-fruit', 'sour', 'farmhouse'],
  }),
  fruitPuree({
    id: 'apricot-puree',
    name: 'Apricot Purée',
    extractFineDryPercent: 28,
    colorLovibond: 5,
    description:
      'A shade denser and more orange than peach, with a sharper dried-fruit edge. Classic in American wild ales and saison; the sugar is gone by packaging and the apricot reads as aroma rather than sweetness.',
    tags: ['fruit', 'apricot', 'puree', 'stone-fruit', 'sour', 'saison'],
  }),
  fruitPuree({
    id: 'mango-puree',
    name: 'Mango Purée',
    extractFineDryPercent: 32,
    colorLovibond: 6,
    description:
      'Tropical and a little thicker than the berry purées, so the gravity bump is real even if it is still mostly water. Milkshake IPAs, kettle sours and pastry stouts all use it; the flesh can haze a beer for good. Add post-fermentation.',
    tags: ['fruit', 'mango', 'puree', 'tropical', 'ipa', 'sour'],
  }),
  fruitPuree({
    id: 'passionfruit-puree',
    name: 'Passionfruit Purée',
    extractFineDryPercent: 28,
    colorLovibond: 7,
    description:
      'Tart, aromatic and loud at small doses — a few percent of the grist is enough. The sugars ferment out and leave the tropical-acid punch gose, kettle sours and hazy IPAs are after. Boiling it is a waste of the perfume.',
    tags: ['fruit', 'passionfruit', 'puree', 'tropical', 'sour', 'gose'],
  }),
  fruitPuree({
    id: 'pineapple-puree',
    name: 'Pineapple Purée',
    extractFineDryPercent: 27,
    colorLovibond: 2,
    description:
      'Pale, juicy and almost colorless in the glass. Gravity contribution is modest; the character is a bright tropical acidity that suits gose, kettle sours and hoppy pales. Proteolytic enzymes in raw pineapple are already gone in an aseptic pack.',
    tags: ['fruit', 'pineapple', 'puree', 'tropical', 'sour', 'gose'],
  }),
  fruitPuree({
    id: 'banana-puree',
    name: 'Banana Purée',
    extractFineDryPercent: 38,
    colorLovibond: 3,
    description:
      'The densest common fruit purée: more sugar than a berry pack, so the gravity bump is the one you actually notice. Pastry stouts and milkshake IPAs are the usual destination. The esters overlap a weizen yeast, so it is easy to overdo in a wheat beer.',
    tags: ['fruit', 'banana', 'puree', 'tropical', 'pastry', 'stout'],
  }),
  fruitPuree({
    id: 'blackcurrant-puree',
    name: 'Blackcurrant Purée',
    extractFineDryPercent: 26,
    colorLovibond: 22,
    description:
      'Cassis: dark, tart and a little tannic. Stains a pale beer inky and leaves a dry berry-skin finish once the sugars are gone. Dark sours, imperial stouts and the odd Flanders-style blend are where it earns its keep.',
    tags: ['fruit', 'blackcurrant', 'puree', 'berry', 'sour', 'cassis'],
  }),
  fruitPuree({
    id: 'apple-puree',
    name: 'Apple Purée',
    extractFineDryPercent: 27,
    colorLovibond: 2,
    description:
      'Aseptic apple, pale and quietly fermentable. It will not shout the way raspberry does; the point is a cider-adjacent body and a soft fruit acidity in sours, farmhouse ales and the occasional pale. Not a substitute for actual cider juice.',
    tags: ['fruit', 'apple', 'puree', 'sour', 'farmhouse', 'cider'],
  }),
  fruitPuree({
    id: 'cranberry-puree',
    name: 'Cranberry Purée',
    extractFineDryPercent: 22,
    colorLovibond: 8,
    description:
      'Lean, tart and low in sugar even by purée standards. The color is a clean red and the flavor is more acid than fruit, which is why holiday sours, goses and the odd winter warmer use it. Easy to make a beer taste medicinal if you chase the color.',
    tags: ['fruit', 'cranberry', 'puree', 'tart', 'sour', 'gose'],
  }),
  fruitPuree({
    id: 'plum-puree',
    name: 'Plum Purée',
    extractFineDryPercent: 28,
    colorLovibond: 12,
    description:
      'Dark-skin plum, denser than peach and more tannic. The sugars ferment out and leave a dried-stone-fruit note that Belgian darks, Flanders reds and barrel-aged sours already hint at. Add after primary so the skin character survives.',
    tags: ['fruit', 'plum', 'puree', 'stone-fruit', 'sour', 'belgian'],
  }),
  fruitPuree({
    id: 'blood-orange-puree',
    name: 'Blood Orange Purée',
    extractFineDryPercent: 25,
    colorLovibond: 10,
    description:
      'Orange flesh with a raspberry-red tint. The sugars ferment; the pith and zest character is what remains, so it reads as citrus rather than juice. Gose, kettle sours and hoppy pales are the usual home. Not a substitute for actual orange zest in the whirlpool.',
    tags: ['fruit', 'orange', 'puree', 'citrus', 'sour', 'gose'],
  }),
  // -------------------------------------------------------------------------
  // Alternate and ancient grains
  //
  // Two groups of brewers want these and they want them for opposite reasons.
  //
  // **Gluten-free brewers** have no choice: sorghum, millet, buckwheat, quinoa,
  // amaranth, teff, rice and fonio are the whole pantry, and a beer built from
  // them is a different problem rather than a substitution. Most carry little
  // or no diastatic power even when malted, so a grist of them needs exogenous
  // enzyme or a malted-millet base with enough of its own to convert the rest.
  //
  // **Everyone else** reaches for the ancient wheats — spelt, emmer, einkorn,
  // khorasan — for flavour and for the awkward husk-and-protein behaviour that
  // comes with them.
  //
  // Every figure here is a class mid-point and `representative` by the file's
  // own rule, and these deserve the caveat more than most: published analyses
  // of malted pseudo-cereals are thin, vary widely by maltster and crop, and
  // the extract of a malted pseudo-cereal in particular is closer to a range
  // than a number. Design against them, then correct from the bag.
  {
    id: 'malted-sorghum',
    name: 'Malted Sorghum',
    maker: 'Generic',
    origin: 'Various',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 74,
    moisturePercent: 8,
    colorLovibond: 2.5,
    colorEbc: 5.42,
    diastaticPowerLintner: 30,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The workhorse of gluten-free brewing, and the one that will carry a grist on its own. Grainy and faintly sour, with a nuttiness that reads as "beer" more readily than the other pseudo-cereals. Diastatic power is real but modest, so a long low rest or an enzyme addition is usual.',
    tags: ['base', 'sorghum', 'gluten-free', 'alternate-grain'],
    provenance: 'representative',
  },
  {
    id: 'malted-millet',
    name: 'Malted Millet',
    maker: 'Generic',
    origin: 'Various',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 75,
    moisturePercent: 8,
    colorLovibond: 2,
    colorEbc: 4.1,
    diastaticPowerLintner: 45,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Sweet, honeyed and clean, and the gluten-free base with the most enzyme of its own — enough to convert a grist that is mostly millet. The husk is negligible, so a mash of it sets like porridge without rice hulls.',
    tags: ['base', 'millet', 'gluten-free', 'alternate-grain'],
    provenance: 'representative',
  },
  {
    id: 'malted-buckwheat',
    name: 'Malted Buckwheat',
    maker: 'Generic',
    origin: 'Various',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 70,
    moisturePercent: 9,
    colorLovibond: 8,
    colorEbc: 20,
    diastaticPowerLintner: 10,
    suggestedMaxPercent: 40,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Not a cereal at all but a knotweed seed, which is why it is gluten-free and why it tastes the way it does: earthy, toasted and distinctly of buckwheat pancakes. Assertive enough that it is usually a flavor grain rather than a base, even in a gluten-free grist.',
    tags: ['buckwheat', 'gluten-free', 'alternate-grain', 'pseudo-cereal'],
    provenance: 'representative',
  },
  {
    id: 'malted-fonio',
    name: 'Malted Fonio',
    maker: 'Generic',
    origin: 'West Africa',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 72,
    moisturePercent: 9,
    colorLovibond: 2.5,
    colorEbc: 5.42,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A West African millet relative, one of the oldest cultivated cereals on the continent and the fastest to ripen. Delicate and nutty with a light sweetness, cleaner than sorghum and less honeyed than millet. Gluten-free, low in enzyme, and very small-grained, so mill it fine and expect a slow runoff.',
    tags: ['base', 'fonio', 'gluten-free', 'alternate-grain', 'african'],
    provenance: 'representative',
  },
  {
    id: 'malted-quinoa',
    name: 'Malted Quinoa',
    maker: 'Generic',
    origin: 'Various',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 68,
    moisturePercent: 9,
    colorLovibond: 6,
    colorEbc: 14.7,
    diastaticPowerLintner: 15,
    suggestedMaxPercent: 25,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Grassy and herbal with a bitter edge that comes from the saponins in the seed coat; well-rinsed quinoa is much milder than badly-rinsed quinoa. High in protein and oil, which builds head and hurts stability in equal measure, so it is a supporting grain rather than a base.',
    tags: ['quinoa', 'gluten-free', 'alternate-grain', 'pseudo-cereal'],
    provenance: 'representative',
  },
  {
    id: 'malted-amaranth',
    name: 'Malted Amaranth',
    maker: 'Generic',
    origin: 'Various',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 66,
    moisturePercent: 9,
    colorLovibond: 7,
    colorEbc: 17.35,
    diastaticPowerLintner: 10,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Peppery and malty with a distinct earthiness, and a tiny seed that behaves in the mash more like flour than grain. Used in small proportions for the flavor and the protein it lends the head.',
    tags: ['amaranth', 'gluten-free', 'alternate-grain', 'pseudo-cereal'],
    provenance: 'representative',
  },
  {
    id: 'malted-teff',
    name: 'Malted Teff',
    maker: 'Generic',
    origin: 'Ethiopia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 68,
    moisturePercent: 9,
    colorLovibond: 12,
    colorEbc: 30.6,
    diastaticPowerLintner: 10,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The Ethiopian grain behind injera, and it brings some of that sourdough character with it: molasses, cocoa and a faint tang. The smallest cereal grain there is, so it passes straight through a normal mill gap and needs rice hulls to lauter at all.',
    tags: ['teff', 'gluten-free', 'alternate-grain', 'ethiopian'],
    provenance: 'representative',
  },
  {
    id: 'malted-spelt',
    name: 'Malted Spelt',
    maker: 'Generic',
    origin: 'Various',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 79,
    moisturePercent: 6,
    colorLovibond: 2.5,
    colorEbc: 5.42,
    diastaticPowerLintner: 65,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'An ancient hulled wheat, nutty and faintly sweet with a silkier body than modern wheat. Contains gluten. The hull helps the lauter where malted wheat hinders it, which is much of why Belgian brewers keep coming back to it.',
    tags: ['base', 'spelt', 'ancient-grain', 'wheat', 'belgian'],
    provenance: 'representative',
  },
  {
    id: 'malted-emmer',
    name: 'Malted Emmer',
    maker: 'Generic',
    origin: 'Various',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 76,
    moisturePercent: 7,
    colorLovibond: 3,
    colorEbc: 6.75,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 40,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Farro: one of the founder crops of agriculture, and still one of the most characterful grains in a mash. Deeply nutty and bready, more assertive than spelt. Hulled, high in protein, and slow to convert. Contains gluten.',
    tags: ['base', 'emmer', 'farro', 'ancient-grain', 'wheat'],
    provenance: 'representative',
  },
  {
    id: 'malted-einkorn',
    name: 'Malted Einkorn',
    maker: 'Generic',
    origin: 'Various',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 74,
    moisturePercent: 7,
    colorLovibond: 3.5,
    colorEbc: 8.08,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The oldest domesticated wheat, and the smallest yield of the lot, which is why it is rare and expensive. Rich and almost buttery, with a yellow tint from its carotenoids. Contains gluten, though a different and simpler gluten than modern wheat.',
    tags: ['base', 'einkorn', 'ancient-grain', 'wheat'],
    provenance: 'representative',
  },
  {
    id: 'malted-khorasan',
    name: 'Malted Khorasan',
    maker: 'Generic',
    origin: 'Various',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 78,
    moisturePercent: 7,
    colorLovibond: 2.5,
    colorEbc: 5.42,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 40,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The large-grained ancient wheat sold as Kamut, buttery and sweet with a smooth body. The kindest of the ancient wheats to work with: the grain is big, it mills predictably and it converts readily. Contains gluten.',
    tags: ['base', 'khorasan', 'kamut', 'ancient-grain', 'wheat'],
    provenance: 'representative',
  },
  // -------------------------------------------------------------------------
  // The malt classes a recipe file names
  //
  // A BeerXML file that says `Pilsner Malt`, `Crystal 60` or `Chocolate Malt`
  // and nothing else has named a *class*, and until these rows existed there
  // was nothing in the catalog for such a name to land on: the fifty Generic
  // entries above were all adjuncts, sugars and syrups, so an import went to
  // whichever maltster's pilsner scored first — which is a craft house the
  // brewer has never heard of and cannot buy. These are the rows it lands on
  // instead, and the importer's maker-reach rule (see
  // `data/fermentables/makers.ts`) is what sends it here.
  //
  // **Where the numbers come from.** Every figure is the *median of the
  // catalog's own rows of that class* — not a textbook, not an average of
  // guesses, and not one maltster's sheet standing in for the rest. Where that
  // is a wobbly statistic the selection is stated in the row's own comment. The
  // three standing rules:
  //
  // - **A crystal grade's colour is the grade**, exactly: `Crystal 60` is
  //   60 °L, because the number in the name is the product. Extract and the
  //   grist ceiling are the median of the crystal rows within 25 % of that
  //   grade; moisture is the median of the whole crystal class (5.5 %), because
  //   per-grade moisture is noise on four rows.
  // - **Diastatic power follows the class**, so the crystal and roasted rows
  //   are zero and the base rows carry the median of the malts they stand for.
  // - **`provenance: 'representative'`, `maker: 'Generic'`, no `url`.** There is
  //   no product page for a class, and a link would be a claim about a company
  //   nobody named.
  //
  // A brewer holding the actual bag should replace these with the branded row,
  // which is one **Replace with…** away and carries the maltster's own figures.
  {
    id: 'generic-pilsner',
    name: 'Pilsner Malt',
    maker: 'Generic',
    origin: 'Various',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    colorEbc: 3.6,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "The class rather than anybody's product: continental lager malt kilned barely past drying, pale enough to leave a pilsner straw-colored and enzymic enough to carry a grist of adjuncts. The median of the eighty-odd pilsner malts this catalog carries. A named maltster's row will be a shade paler or darker and a few points either way on extract.",
    tags: ['base', 'pilsner', 'lager', 'pale'],
    provenance: 'representative',
  },
  {
    id: 'generic-2-row-pale',
    name: '2-Row Pale Malt',
    maker: 'Generic',
    origin: 'Various',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.1,
    moisturePercent: 4.5,
    colorLovibond: 2,
    colorEbc: 4.1,
    diastaticPowerLintner: 130,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "The North American brewers' malt, as a class: two-row barley malted for a clean, faintly sweet base and a diastatic power high enough to convert half a grist of corn or rice beside it. What `Pale Malt (2 Row)` means in a recipe file when the file does not say whose.",
    tags: ['base', 'two-row', 'pale', 'high-enzyme'],
    provenance: 'representative',
  },
  {
    id: 'generic-pale-ale',
    name: 'Pale Ale Malt',
    maker: 'Generic',
    origin: 'Various',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.5,
    moisturePercent: 4.5,
    colorLovibond: 3,
    colorEbc: 6.8,
    diastaticPowerLintner: 75,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Base malt kilned a step past pilsner, which is the whole difference: a little more color, a little bread crust, a little less enzyme. The class every ale grist is built on, and the median of the thirty-four pale ale malts here. Not a substitute for naming your maltster, but the right shape when the file does not.',
    tags: ['base', 'pale-ale', 'ale'],
    provenance: 'representative',
  },
  {
    id: 'generic-maris-otter',
    name: 'Pale Ale Malt (Maris Otter)',
    maker: 'Generic',
    origin: 'United Kingdom',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80.6,
    moisturePercent: 4,
    colorLovibond: 2.6,
    colorEbc: 5.7,
    diastaticPowerLintner: 48,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Maris Otter is a barley variety, not a brand, and six maltsters sell a pale ale malt made from it — which is why a recipe naming it names a class. Lower in enzyme than a modern high-yield variety and richer for it: the nutty, biscuit depth English bitter is built on. The median of the eleven Maris Otter rows here.',
    tags: ['base', 'maris-otter', 'pale-ale', 'heritage', 'british'],
    provenance: 'representative',
  },
  {
    id: 'generic-munich',
    name: 'Munich Malt',
    maker: 'Generic',
    origin: 'Various',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 8.4,
    colorEbc: 21.1,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 80,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned warm and wet enough to build melanoidins without stewing the starch, which is what makes it malty rather than sweet. The class sits between the Type I and Type II a German maltster splits it into, so a file that says only `Munich` lands here rather than on a guess at which. Keeps enough enzyme to convert itself.',
    tags: ['munich', 'kilned', 'malty', 'amber'],
    provenance: 'representative',
  },
  {
    id: 'generic-vienna',
    name: 'Vienna Malt',
    maker: 'Generic',
    origin: 'Various',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79.7,
    moisturePercent: 4.5,
    colorLovibond: 4,
    colorEbc: 9.4,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A half-step of kilning between pale ale malt and Munich: golden, lightly toasty, and enzymic enough to be the whole grist of a Vienna lager or a märzen. The class as forty-four maltsters here write it, which is a narrower band than Munich because there is only one Vienna.',
    tags: ['vienna', 'kilned', 'lager', 'golden'],
    provenance: 'representative',
  },
  {
    id: 'generic-wheat-malt',
    name: 'Wheat Malt',
    maker: 'Generic',
    origin: 'Various',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 5,
    colorLovibond: 2.4,
    colorEbc: 5.2,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted wheat as a class: higher extract than barley malt, no husk to lauter with, and the protein that gives a weissbier its haze and a lasting head. Softer and breadier than flaked wheat, which is the unmalted grain. Above about half the grist, expect a slow run-off unless rice hulls go in with it.',
    tags: ['wheat', 'base', 'head-retention', 'hefeweizen'],
    provenance: 'representative',
  },
  {
    id: 'generic-rye-malt',
    name: 'Rye Malt',
    maker: 'Generic',
    origin: 'Various',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 3.5,
    colorEbc: 8.1,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "The class, from the median of thirty-seven maltsters' rows: huskless malted rye, spicy and faintly sour, with the drying grip that makes a rye IPA read as leaner than its gravity. The beta-glucans are serious — a quarter of the grist is a lively mash and half of it wants rice hulls.",
    tags: ['rye', 'base', 'spicy'],
    provenance: 'representative',
  },
  {
    id: 'generic-crystal-10',
    name: 'Crystal 10',
    maker: 'Generic',
    origin: 'Various',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5.5,
    colorLovibond: 10,
    colorEbc: 25.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest of the crystal grades: stewed to set the sugars and kilned barely at all, so it adds body and a faint honey sweetness without moving the color. Sold as Caramel 10L as often as Crystal 10, and the two names mean the same malt.',
    tags: ['crystal', 'caramel', 'light', 'body'],
    provenance: 'representative',
  },
  {
    id: 'generic-crystal-20',
    name: 'Crystal 20',
    maker: 'Generic',
    origin: 'Various',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75.5,
    moisturePercent: 5.5,
    colorLovibond: 20,
    colorEbc: 51.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Light crystal, the grade that sweetens and rounds a pale ale without darkening it past gold. Honey and light toffee; the median of the two dozen catalog rows that sit within a quarter of 20 °L.',
    tags: ['crystal', 'caramel', 'light', 'honey'],
    provenance: 'representative',
  },
  {
    id: 'generic-crystal-40',
    name: 'Crystal 40',
    maker: 'Generic',
    origin: 'Various',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5.5,
    colorLovibond: 40,
    colorEbc: 104.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The default crystal of American brewing: amber, toffee-sweet, and the grade a recipe means when it says only "crystal malt". Five per cent lifts a pale ale; fifteen makes an amber.',
    tags: ['crystal', 'caramel', 'toffee', 'amber'],
    provenance: 'representative',
  },
  {
    id: 'generic-crystal-60',
    name: 'Crystal 60',
    maker: 'Generic',
    origin: 'Various',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5.5,
    colorLovibond: 60,
    colorEbc: 157.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Medium crystal — the English bitter and brown ale grade, deeper than the amber crystals and beginning to read as dried fruit rather than toffee. Forty-nine rows here sit within a quarter of it, which is why a file that says `Crystal 60` and nothing else needed somewhere honest to land.',
    tags: ['crystal', 'caramel', 'toffee', 'brown-ale'],
    provenance: 'representative',
  },
  {
    id: 'generic-crystal-80',
    name: 'Crystal 80',
    maker: 'Generic',
    origin: 'Various',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 5.5,
    colorLovibond: 80,
    colorEbc: 210.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Dark crystal: raisin and burnt sugar, with the ruby cast that puts the red in a red ale. Past this grade the sweetness starts to turn towards the astringency of the very dark caramels, so it is dosed in single figures more often than not.',
    tags: ['crystal', 'caramel', 'dark', 'raisin'],
    provenance: 'representative',
  },
  {
    id: 'generic-crystal-120',
    name: 'Crystal 120',
    maker: 'Generic',
    origin: 'Various',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 73,
    moisturePercent: 5.5,
    colorLovibond: 120,
    colorEbc: 316.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest crystal in ordinary use: prune, fig and a scorched edge, dosed at a few per cent in old ales, barleywines and dark milds. More than that and the beer tastes of the malt rather than of itself.',
    tags: ['crystal', 'caramel', 'dark', 'prune'],
    provenance: 'representative',
  },
  {
    id: 'generic-chocolate',
    name: 'Chocolate Malt',
    maker: 'Generic',
    origin: 'Various',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 350,
    colorEbc: 926.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Pale malt roasted to the point where the cocoa and coffee arrive but the acrid edge has not: the color and flavor of a porter, and the milder half of a stout grist. The class, from twenty-nine catalog rows; a named maltster's will land anywhere between 300 and 450 °L.",
    tags: ['roasted', 'chocolate', 'porter', 'cocoa'],
    provenance: 'representative',
  },
  {
    id: 'generic-black',
    name: 'Black Malt',
    maker: 'Generic',
    origin: 'Various',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4.5,
    colorLovibond: 500,
    colorEbc: 1323.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Roasted to the far end, where the flavor is ash and espresso and the job is mostly color. Sold as black malt and as patent malt, which is the same thing under the 1817 patent that named it. Two or three per cent turns a beer black; five is as far as most grists go.',
    tags: ['roasted', 'black', 'stout', 'colour'],
    provenance: 'representative',
  },
  {
    id: 'generic-carapils',
    name: 'Carapils/Dextrin Malt',
    maker: 'Generic',
    origin: 'Various',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74.5,
    moisturePercent: 6.5,
    colorLovibond: 2,
    colorEbc: 4.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      "A crystal malt stewed and dried without being kilned, so it carries the unfermentable dextrins and none of the color. Bought for body and head retention rather than for flavor. `Carapils` is Briess's trademark and `Dextrin Malt` is what everyone else calls it; the class is both, and the figures are the median of the dozen dextrin rows here.",
    tags: ['dextrin', 'carapils', 'body', 'head-retention'],
    provenance: 'representative',
  },
  {
    id: 'generic-aromatic',
    name: 'Aromatic Malt',
    maker: 'Generic',
    origin: 'Various',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 20,
    colorEbc: 51.8,
    diastaticPowerLintner: 18.5,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Munich kilned harder — a dark, intensely malty aroma malt from the Belgian tradition, used at five to ten per cent where a grist needs to smell of malt without going sweet. Only four maltsters here sell one, so the class is a narrow median and worth correcting from the bag.',
    tags: ['aromatic', 'kilned', 'malty', 'belgian'],
    provenance: 'representative',
  },
  {
    id: 'generic-biscuit',
    name: 'Biscuit Malt',
    maker: 'Generic',
    origin: 'Various',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 75.5,
    moisturePercent: 4.5,
    colorLovibond: 19.3,
    colorEbc: 50,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "Lightly roasted rather than kilned, which is what puts the toast and digestive-biscuit note in a brown ale or a dubbel. No enzyme left, so it rides along in the mash on somebody else's. The class, from the sixteen kilned biscuit rows here, which agree with each other closely.",
    tags: ['biscuit', 'toasted', 'bready', 'brown-ale'],
    provenance: 'representative',
  },
  {
    id: 'generic-smoked',
    name: 'Smoked Malt',
    maker: 'Generic',
    origin: 'Various',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 3.5,
    colorEbc: 8.1,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 60,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Base malt dried over a wood fire, which is a process rather than a product: the wood decides everything and the class cannot say which. Beech is the Bamberg rauchbier default; alder, cherry, peat and manuka are all sold. A file that says only `Smoked Malt` means this, and the bag on the shelf will say what it was smoked over.',
    tags: ['smoked', 'rauchbier', 'base', 'specialty'],
    provenance: 'representative',
  },
  {
    id: 'generic-acidulated',
    name: 'Acidulated Malt',
    maker: 'Generic',
    origin: 'Various',
    type: 'base',
    maltClass: 'acidulated',
    extractFineDryPercent: 77,
    moisturePercent: 8,
    colorLovibond: 3.5,
    colorEbc: 8.1,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pilsner malt sprayed with its own lactic acid and dried, which is the Reinheitsgebot-legal way to acidify a mash. One per cent of the grist drops the mash roughly a tenth of a pH point. No usable enzyme of its own despite being sold as a base malt. Sauermalz and sour malt are the same thing.',
    tags: ['acidulated', 'sauermalz', 'mash-ph', 'ph'],
    provenance: 'representative',
  },
];
